import Api from 'services/api'

/**
 * The company's one investor directory.
 *
 * Loan participants, loan purchasers and fund investors used to be three separate directories with
 * three separate records of the same person. They are one pool now: one record, one login, one set
 * of payout banking, and whichever of the three roles apply. These endpoints are what Manage
 * Investors reads and writes; the older `/participants` and `/investors` routes still answer for
 * the screens that have not moved.
 */
const API_INVESTOR_DIRECTORY = '/investorDirectory'
const API_INVESTOR_DIRECTORY_BY_ID = '/investorDirectory/:id'
const API_LENDER_CHARGE = '/loan/lenderCharge'
const API_LENDER_CHARGE_BY_ID = '/loan/lenderCharge/:id'
const API_LENDER_CHARGE_SETTLE = '/loan/lenderCharge/:id/settle'
const API_LENDER_CHARGE_SETTLEMENTS = '/loan/lenderCharge/:id/settlement'
const API_LENDER_CHARGE_SETTLEMENT_BY_ID = '/loan/lenderCharge/settlement/:recoveryId'

/** Which of the three things an investor currently is. Derived from what they hold, never stored. */
export interface IInvestorRoles {
  /** Buys loans out of origination. */
  origination: boolean
  /** Holds a slice of a serviced loan. */
  servicing: boolean
  /** Holds capital in a mortgage fund. */
  fund: boolean
}

export enum InvestorPortalStatus {
  Active = 1,
  /** Cannot sign in to the portal. Where every converted or newly linked investor starts. */
  Pending = 0,
  /** The lead lender's own record. Signs in like Active. */
  Leading = -1,
}

export interface IDirectoryInvestor {
  id: number
  no: number
  fullName: string
  email: string
  phone: string
  presentAddress: string
  title: string
  investorCode: number
  taxId: string
  investorType: string
  notes: string
  status: InvestorPortalStatus
  payeeName: string
  bankName: string
  accountRoutingNo: string
  accountNo: string
  accountType: string
  achActive: boolean
  contacts: Record<string, any>[]
  internalContacts: string[]
  createdAt: string
  /** Distinct loans they are on, counted across participations, assignments and distributions. */
  loans: number
  fundCount: number
  fundValue: number
  roles: IInvestorRoles
}

export const filterInvestorDirectory = (filters: Record<string, any>) => {
  return Api.get(API_INVESTOR_DIRECTORY, filters)
}

export const getInvestorDirectoryCodes = () => {
  return Api.get(`${API_INVESTOR_DIRECTORY}/codes`)
}

export const getInvestorRecord = (id: number) => {
  return Api.get(API_INVESTOR_DIRECTORY_BY_ID, {}, { id })
}

export const createInvestorRecord = (data: Record<string, any>) => {
  return Api.post(API_INVESTOR_DIRECTORY, data)
}

export const updateInvestorRecord = (id: number, data: Record<string, any>) => {
  return Api.put(API_INVESTOR_DIRECTORY_BY_ID, data, { id })
}

export const deleteInvestorRecord = (id: number) => {
  return Api.delete(API_INVESTOR_DIRECTORY_BY_ID, {}, { id })
}

/** Portal access on or off. Pending cannot sign in; Active can. */
export const updateInvestorRecordStatus = (id: number, status: number) => {
  return Api.put(`${API_INVESTOR_DIRECTORY_BY_ID}/status`, { status }, { id })
}

export const updateInvestorRecordPassword = (id: number, password: string) => {
  return Api.post(`${API_INVESTOR_DIRECTORY_BY_ID}/password`, { password }, { id })
}

export const sendInvestorRecordEmail = (id: number) => {
  return Api.post(`${API_INVESTOR_DIRECTORY_BY_ID}/email`, {}, { id })
}

/** Cross-loan distribution history, one page at a time: a converted book runs to tens of thousands. */
export const getInvestorRecordDistributions = (id: number, pageNum = 0, count = 25) => {
  return Api.get(`${API_INVESTOR_DIRECTORY_BY_ID}/distributions`, { pageNum, count }, { id })
}

export const getInvestorRecordLoans = (id: number) => {
  return Api.get(`${API_INVESTOR_DIRECTORY_BY_ID}/loans`, {}, { id })
}

/** Investors a fund can take a position for: active, and not already on the fund side. */
export const getFundInvestorCandidates = (query = '') => {
  return Api.get(`${API_INVESTOR_DIRECTORY}/fundCandidates`, { query })
}

/** Give a directory investor the fund role; returns the fund-side id the fund screens use. */
export const addInvestorFundRole = (id: number) => {
  return Api.post(`${API_INVESTOR_DIRECTORY_BY_ID}/fundRole`, {}, { id })
}

/**
 * Give every pre-merge record a directory entry. Dry run unless `apply` is passed, and idempotent
 * either way, so it is safe to run to see what the merge would do first.
 */
export const linkInvestorDirectory = (apply = false) => {
  return Api.post(`${API_INVESTOR_DIRECTORY}/link`, { apply })
}

/** One lender charge as the investor's Charges section reads it. */
export interface ILenderCharge {
  id: number
  loanNumber: number
  date: string
  description: string
  category: string
  unpaidBalance: number | string
  accruedInterest: number | string
  /** Unpaid balance plus the interest booked against it: what is still owed. */
  due: number
  /** Recovered so far, net of reversals. */
  recovered: number
  isPaid: boolean
  /** True when the charge belongs to no loan (the backend stores loanNumber 0). */
  isStandalone: boolean
  borrowerRecoverable: boolean
  /** The borrower charge a recoverable one bills on, when it has been booked. */
  borrowerCharge: { id: number; unpaidBalance: number; isPaid: boolean } | null
}

/**
 * One investor's lender charges, loan-related and standalone together.
 *
 * Sent with both the id and the name: a charge booked against the loan's own Lender usually has no
 * investor record to key on and carries only a name, and the directory id is the participant id the
 * backend matches on.
 */
export const getLenderCharges = (
  participantUserId: number,
  lenderName: string,
  scope: 'all' | 'standalone' | 'loan' = 'all',
) => {
  return Api.get(API_LENDER_CHARGE, { participantUserId, lenderName, scope })
}

/** Book a standalone lender charge: owed by this investor, about no particular loan. */
export const createStandaloneLenderCharge = (data: Record<string, any>) => {
  return Api.post(API_LENDER_CHARGE, data)
}

export const updateStandaloneLenderCharge = (id: number, data: Record<string, any>) => {
  return Api.put(API_LENDER_CHARGE_BY_ID, data, { id })
}

export const deleteLenderCharge = (id: number) => {
  return Api.delete(API_LENDER_CHARGE_BY_ID, {}, { id })
}

/** Settle a lender charge by hand - the only way a standalone charge is ever paid down. */
export const settleLenderCharge = (
  id: number,
  data: { amount?: number; date?: string; note?: string; markPaid?: boolean },
) => {
  return Api.post(API_LENDER_CHARGE_SETTLE, data, { id })
}

/** Every recovery against one charge: distribution, borrower payment or manual settlement. */
export const getLenderChargeSettlements = (id: number) => {
  return Api.get(API_LENDER_CHARGE_SETTLEMENTS, {}, { id })
}

/** Undo a manual settlement. One from a payment is undone by reversing that payment. */
export const undoLenderChargeSettlement = (recoveryId: number) => {
  return Api.delete(API_LENDER_CHARGE_SETTLEMENT_BY_ID, {}, { recoveryId })
}
